/**
 * 메일의 인증 링크가 도착하는 곳. **하는 일은 하나다** — 토큰으로 인증을 기록하고 결과를 보여준다.
 * 화면도 그만큼만 담백하게 둔다(히어로·안내 타일 없음) — 사용자는 결과만 보고 원래 화면(가입 신청서·대행사 설정)으로 돌아간다.
 *
 * 신청서를 대신 제출하지도, 탭 사이로 신호를 보내지도 않는다. 신청서 탭이 몇 초마다 서버에 물어 스스로 바뀐다
 * (다른 창·휴대폰에서 링크를 눌러도 같다).
 */
import { signup } from '@geo-admin/shared/contract/api/index.ts';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { AlertIcon, CheckIcon } from '../components/icons.tsx';
import { LogoMark } from '../components/LogoMark.tsx';
import { ApiError, call } from '../lib/call.ts';
import { SHELL } from '../lib/branding.ts';

/** 실패 사유는 한 문장으로 — 검증 원문(`token: Too small…`)을 사람에게 보이지 않는다. 속도 제한만 서버 문구를 쓴다 */
const LINK_FAILED = '링크가 만료됐거나 이미 사용됐습니다. 인증을 요청한 화면에서 인증번호를 다시 받아 주세요.';

export function VerifyEmailPage() {
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'ok'; email: string } | { kind: 'fail'; message: string }
  >({
    kind: 'loading',
  });
  const started = useRef(false);

  // 진입 시 1회만 — 토큰은 1회용이라 두 번 부르면 두 번째가 실패한다
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(window.location.search).get('t') ?? '';
    // 토큰을 주소창·방문 기록에서 지운다 (Referer 로도 새지 않게)
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) {
      setState({ kind: 'fail', message: LINK_FAILED });
      return;
    }
    void call(signup.verifyEmailLink, { body: { token } })
      .then((r) => setState({ kind: 'ok', email: r.maskedEmail }))
      .catch((e: Error) =>
        setState({
          kind: 'fail',
          message: e instanceof ApiError && e.code === 'rate-limited' ? e.message : LINK_FAILED,
        }),
      );
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4 py-10 font-suit">
      <div className="w-full max-w-sm text-center">
        <LogoMark tone="onLight" size="sm" />
        <div className="mt-10">
          {state.kind === 'loading' ? (
            <p className="text-slate-500 text-sm">인증하는 중…</p>
          ) : state.kind === 'ok' ? (
            <>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckIcon className="h-6 w-6" strokeWidth={3} />
              </span>
              <h1 className="mt-4 font-bold text-xl">이메일 인증이 완료됐습니다</h1>
              <p className="mt-1 text-slate-500 text-sm">{state.email}</p>
              <p className="mt-6 break-keep text-slate-600 text-sm leading-relaxed">
                이 창을 닫고 작성하시던 화면으로 돌아가 주세요. 인증 표시는 자동으로 바뀝니다.
              </p>
            </>
          ) : (
            <>
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                <AlertIcon className="h-6 w-6" />
              </span>
              <h1 className="mt-4 font-bold text-xl">인증하지 못했습니다</h1>
              <p className="mt-6 break-keep text-slate-600 text-sm leading-relaxed">{state.message}</p>
              <Link to="/signup" className="mt-4 inline-block font-semibold text-live text-sm hover:underline">
                가입 신청 화면으로
              </Link>
            </>
          )}
        </div>
      </div>
      <p className="mt-16 text-slate-400 text-xs">
        문의 {[SHELL.support.tel, SHELL.support.email].filter(Boolean).join(' · ')}
      </p>
    </div>
  );
}
